import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { AssigneeOption, TicketUser } from '@solidgroup-agency/ticket-system/models'
import { AwoTicketUser, isAwoTicketUser } from './awo-assignee.provider'
import { RecentAssigneesService, RecentAssignee } from './recent-assignees.service'
import { avatarGradient, initialsFromName } from './awo-assignee-avatar.utils'

type FilterMode = 'recommended' | 'all' | 'admin' | 'manager'

interface RecentDisplay extends RecentAssignee {
  initials: string
  background: string
  user: AwoTicketUser
}

interface PickerEntry {
  user: AwoTicketUser
  isMatch: boolean
  isCurrent: boolean
  initials: string
  background: string
}

@Component({
  selector: 'app-awo-assignee-picker',
  standalone: true,
  imports: [MatIconModule, MatMenuModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(click)': '$event.stopPropagation()',
    '(mousedown)': '$event.stopPropagation()',
    '(keydown.enter)': '$event.stopPropagation()',
    '(keydown.space)': '$event.stopPropagation()',
  },
  template: `
    <button
      type="button"
      class="trigger"
      [matMenuTriggerFor]="menu"
      (menuOpened)="onMenuOpened()"
    >
      <span class="avatar avatar-sm" [style.background]="triggerBg()">{{
        triggerInitials()
      }}</span>
      <span class="trigger-name">{{ triggerLabel() }}</span>
      <mat-icon class="arrow">arrow_drop_down</mat-icon>
    </button>

    <mat-menu #menu="matMenu" class="awo-assignee-menu" xPosition="before">
      <ng-template matMenuContent>
        <div class="menu-body">
          <div class="search-row">
            <mat-icon class="search-icon">search</mat-icon>
            <input
              #searchInput
              type="text"
              placeholder="Bearbeiter suchen…"
              [value]="searchTerm()"
              (input)="onSearchInput($event)"
              (click)="$event.stopPropagation()"
              (keydown)="$event.stopPropagation()"
            />
          </div>

          <div class="filter-row">
            <button
              type="button"
              class="filter"
              [class.active]="filterMode() === 'recommended'"
              (click)="setFilter($event, 'recommended')"
            >
              Empfohlen
            </button>
            <button
              type="button"
              class="filter"
              [class.active]="filterMode() === 'all'"
              (click)="setFilter($event, 'all')"
            >
              Alle
            </button>
            <button
              type="button"
              class="filter"
              [class.active]="filterMode() === 'admin'"
              (click)="setFilter($event, 'admin')"
            >
              Admins
            </button>
            <button
              type="button"
              class="filter"
              [class.active]="filterMode() === 'manager'"
              (click)="setFilter($event, 'manager')"
            >
              Manager
            </button>
          </div>

          <div class="quick-actions">
            @if (currentUserUid()) {
              <button type="button" class="quick" (click)="assignSelf()">
                <span class="avatar avatar-sm self">{{ selfInitials() }}</span>
                <span class="quick-text">
                  <span class="quick-title">Mir zuweisen</span>
                  <span class="quick-sub">Schnellaktion</span>
                </span>
              </button>
            }
            <button type="button" class="quick" (click)="clearAssignee()">
              <span class="avatar avatar-sm empty">–</span>
              <span class="quick-text">
                <span class="quick-title">Niemand</span>
                <span class="quick-sub">Zuweisung entfernen</span>
              </span>
            </button>
          </div>

          @if (recentDisplay().length > 0) {
            <div class="group-label">
              <mat-icon class="group-icon">history</mat-icon>
              Zuletzt von dir zugewiesen
              <span class="group-count">· {{ recentDisplay().length }}</span>
            </div>
            @for (r of recentDisplay(); track r.uid) {
              <button type="button" class="row" (click)="select(r.user)">
                <span class="avatar avatar-sm" [style.background]="r.background">{{ r.initials }}</span>
                <span class="row-text">
                  <span class="row-name">{{ r.displayName }}</span>
                  <span class="row-sub">
                    {{ relativeTime(r.lastAssignedAt) }}
                    @if (r.lastTicketId) {
                      · #{{ r.lastTicketId }}
                    }
                  </span>
                </span>
              </button>
            }
          }

          @if (recommendedList().length > 0) {
            <div class="group-label">
              @if (facilityName()) {
                Passend für {{ facilityName() }}
              } @else {
                Empfohlene Bearbeiter
              }
              <span class="group-count">· {{ recommendedList().length }}</span>
            </div>
            @for (e of recommendedList(); track e.user.uid) {
              <button
                type="button"
                class="row"
                [class.selected]="e.isCurrent"
                (click)="select(e.user)"
              >
                <span class="avatar avatar-sm" [style.background]="e.background">{{ e.initials }}</span>
                <span class="row-text">
                  <span class="row-name">{{ e.user.displayName }}</span>
                </span>
                @if (e.isCurrent) {
                  <span class="chip match">✓ aktuell</span>
                }
              </button>
            }
          }

          @if (otherList().length > 0) {
            <div class="group-label">
              Weitere Bearbeiter
              <span class="group-count">· {{ otherList().length }}</span>
            </div>
            @for (e of otherList(); track e.user.uid) {
              <button
                type="button"
                class="row"
                [class.selected]="e.isCurrent"
                (click)="select(e.user)"
              >
                <span class="avatar avatar-sm" [style.background]="e.background">{{ e.initials }}</span>
                <span class="row-text">
                  <span class="row-name">{{ e.user.displayName }}</span>
                </span>
                @if (e.isCurrent) {
                  <span class="chip match">✓ aktuell</span>
                }
              </button>
            }
          }

          @if (recommendedList().length === 0 && otherList().length === 0 && recentDisplay().length === 0) {
            <div class="empty-state">Keine Bearbeiter gefunden.</div>
          }
        </div>
      </ng-template>
    </mat-menu>
  `,
  styles: [`
    :host { display: block; width: 100%; }

    .trigger {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.25rem 0.5rem;
      background: transparent;
      border: 1px solid transparent;
      border-radius: 0.5rem;
      cursor: pointer;
      font: inherit;
      color: inherit;
      text-align: left;
    }
    .trigger:hover {
      border-color: var(--mat-sys-outline-variant);
    }
    .trigger-name {
      flex: 1;
      font-size: 0.875rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .arrow {
      color: var(--mat-sys-on-surface-variant);
      flex-shrink: 0;
    }

    .avatar {
      width: 1.75rem;
      height: 1.75rem;
      border-radius: 999px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-weight: 600;
      font-size: 0.6875rem;
      flex-shrink: 0;
      background: linear-gradient(135deg, #ffb199, #ff5b4d);
    }
    .avatar.self {
      background: linear-gradient(135deg, #5e8bff, #1565c0);
    }
    .avatar.empty {
      background: #cfd4dd;
    }

    .chip {
      display: inline-flex;
      align-items: center;
      padding: 0.125rem 0.5rem;
      border-radius: 999px;
      font-size: 0.6875rem;
      font-weight: 600;
      line-height: 1.6;
    }
    .chip.role-admin { background: #fde7e4; color: #a52316; }
    .chip.role-manager { background: #e3f2fd; color: #0d47a1; }
    .chip.match { background: #e8f5e9; color: #1b5e20; }
  `],
  // styles for the overlay panel (rendered outside :host) live in styles.scss
  // under .awo-assignee-menu
})
export class AwoAssigneePickerComponent {
  private recents = inject(RecentAssigneesService)

  assigneeOptions = input.required<AssigneeOption[]>()
  currentAssigneeUid = input<string | null>(null)
  currentUserUid = input<string | null>(null)
  facilityName = input<string | null>(null)
  /**
   * Callback alternative to the `assigneeSelected` output, for cases where the
   * picker is rendered via `*ngComponentOutlet` (e.g. as a custom cell in a
   * table), which cannot bind outputs.
   */
  onAssigneeSelected = input<((user: TicketUser | null) => void) | null>(null)

  assigneeSelected = output<TicketUser | null>()

  protected searchTerm = signal('')
  protected filterMode = signal<FilterMode>('recommended')
  private searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput')

  private optionsAugmented = computed<PickerEntry[]>(() => {
    const opts = this.assigneeOptions()
    const currentUid = this.currentAssigneeUid()
    const selfUid = this.currentUserUid()
    return opts
      .filter((o) => o.user.uid !== selfUid)
      .map((o) => {
        const user = (
          isAwoTicketUser(o.user)
            ? o.user
            : ({ ...o.user, role: 'manager' as const })
        ) satisfies AwoTicketUser
        return {
          user,
          isMatch: o.isRecommended ?? false,
          isCurrent: user.uid === currentUid,
          initials: initialsFromName(user.displayName),
          background: avatarGradient(user.uid),
        }
      })
  })

  private byFilter(entry: PickerEntry): boolean {
    const term = this.searchTerm().toLowerCase().trim()
    const matchTerm = !term || entry.user.displayName.toLowerCase().includes(term)
    const mode = this.filterMode()
    const matchMode =
      mode === 'all' ||
      mode === 'recommended' ||
      mode === entry.user.role
    return matchTerm && matchMode
  }

  recentDisplay = computed<RecentDisplay[]>(() => {
    const caller = this.currentUserUid()
    if (!caller) return []
    // Beim expliziten Rollen-Filter erwartet der User eine vollstaendige
    // Liste der Rolle, nicht eine separate Recent-Sektion.
    const mode = this.filterMode()
    if (mode === 'admin' || mode === 'manager') return []
    const availableByUid = new Map(this.optionsAugmented().map((e) => [e.user.uid, e.user]))
    const out: RecentDisplay[] = []
    for (const r of this.recents.list(caller)) {
      const user = availableByUid.get(r.uid)
      if (!user) continue
      out.push({
        ...r,
        initials: initialsFromName(user.displayName),
        background: avatarGradient(r.uid),
        user,
      })
      if (out.length === 3) break
    }
    return out
  })

  private recentUidSet = computed(() => new Set(this.recentDisplay().map((r) => r.uid)))

  recommendedList = computed<PickerEntry[]>(() => {
    const mode = this.filterMode()
    const recentUids = this.recentUidSet()
    return this.optionsAugmented()
      .filter((e) => e.isMatch)
      .filter((e) => !recentUids.has(e.user.uid))
      .filter((e) => this.byFilter(e))
      .filter(() => mode === 'recommended' || mode === 'all' || true) // entries already pass byFilter
  })

  otherList = computed<PickerEntry[]>(() => {
    const mode = this.filterMode()
    if (mode === 'recommended') return [] // hidden in recommended-only mode
    const recentUids = this.recentUidSet()
    return this.optionsAugmented()
      .filter((e) => !e.isMatch)
      .filter((e) => !recentUids.has(e.user.uid))
      .filter((e) => this.byFilter(e))
  })

  protected triggerLabel = computed(() => {
    const uid = this.currentAssigneeUid()
    if (!uid) return 'Nicht zugewiesen'
    if (uid === this.currentUserUid()) return 'Mir zugewiesen'
    const opt = this.assigneeOptions().find((o) => o.user.uid === uid)
    return opt?.user.displayName ?? 'Unbekannter Bearbeiter'
  })

  protected triggerInitials = computed(() => {
    const uid = this.currentAssigneeUid()
    if (!uid) return '–'
    if (uid === this.currentUserUid()) return this.selfInitials()
    return initialsFromName(this.triggerLabel())
  })

  protected triggerBg = computed(() => {
    const uid = this.currentAssigneeUid()
    if (!uid) return '#cfd4dd'
    if (uid === this.currentUserUid()) return 'linear-gradient(135deg, #5e8bff, #1565c0)'
    return avatarGradient(uid)
  })

  protected selfInitials = computed(() => {
    const selfUid = this.currentUserUid()
    if (!selfUid) return '?'
    const opt = this.assigneeOptions().find((o) => o.user.uid === selfUid)
    return opt ? initialsFromName(opt.user.displayName) : 'DU'
  })

  onMenuOpened(): void {
    this.searchTerm.set('')
    this.filterMode.set('recommended')
    queueMicrotask(() => this.searchInput()?.nativeElement.focus())
  }

  protected onSearchInput(event: Event): void {
    this.searchTerm.set((event.target as HTMLInputElement).value)
  }

  protected setFilter(event: Event, mode: FilterMode): void {
    event.stopPropagation()
    this.filterMode.set(mode)
  }

  private emitSelection(user: TicketUser | null): void {
    this.assigneeSelected.emit(user)
    this.onAssigneeSelected()?.(user)
  }

  select(user: AwoTicketUser): void {
    this.trackRecent(user)
    this.emitSelection(user)
  }

  assignSelf(): void {
    const selfUid = this.currentUserUid()
    if (!selfUid) return
    const opt = this.assigneeOptions().find((o) => o.user.uid === selfUid)
    if (opt && isAwoTicketUser(opt.user)) this.trackRecent(opt.user)
    this.emitSelection(opt?.user ?? null)
  }

  private trackRecent(user: AwoTicketUser): void {
    const caller = this.currentUserUid()
    if (!caller) return
    this.recents.add(caller, {
      uid: user.uid,
      displayName: user.displayName,
      role: user.role,
    })
  }

  clearAssignee(): void {
    this.emitSelection(null)
  }

  protected relativeTime(iso: string): string {
    const diffMs = Date.now() - Date.parse(iso)
    const minutes = Math.floor(diffMs / 60_000)
    if (minutes < 1) return 'gerade eben'
    if (minutes < 60) return `vor ${minutes} Min`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `vor ${hours} Std`
    const days = Math.floor(hours / 24)
    if (days === 1) return 'gestern'
    return `vor ${days} Tagen`
  }
}
